import { apiFetch } from "@/lib/api";

/**
 * El catálogo de clases de uso de suelo.
 *
 * Con qué se describen los suelos de un predio en la pestaña "Descripción de
 * suelos": cada fila del cuadro elige una clase, le pone una glosa y su
 * superficie.
 *
 * NO ES UN ENUM `I..VIII`, y por eso es un catálogo que se consulta al servidor
 * y no una lista escrita acá. `prototipado/modelo-de-datos.md` §9.2 lo midió
 * contra informes reales de Consultek: el código es COMPUESTO y admite rango
 * —`IIIr-IVr`, `IV-V`, `V-VI`— y el sufijo `r` distingue suelo regado. Su
 * anotación lo dice con todas las letras: "NO es enum I..VIII". Un selector de
 * ocho opciones —que es lo que dibuja el prototipo— rechazaría un código que la
 * empresa ya emitió.
 */

export interface ClaseDeSuelo {
  id: number;
  /**
   * El código tal como se imprime: `III`, `IIIr`, `IIIr-IVr`.
   *
   * A diferencia de `cotizacion_items.codigo` o del `codigo` de un ajuste de
   * homologación —identidad interna que viaja "por si acaso"—, acá el código ES
   * el dato del negocio: es lo que muestra el selector y lo que el cuadro del
   * informe imprime.
   */
  codigo: string;
  /** Qué es esta clase, en palabras. No se imprime en el informe. */
  descripcion: string | null;
  orden: number;
}

export async function fetchClasesDeSuelo(): Promise<ClaseDeSuelo[]> {
  const respuesta = await apiFetch<{ data: ClaseDeSuelo[] }>("/api/suelo-clases");
  return respuesta.data;
}

/** Lo que se decide al crear una clase. El `codigo` solo entra por acá. */
export interface ClaseDeSueloNueva {
  codigo: string;
  descripcion: string | null;
  orden: number;
}

export async function crearClaseDeSuelo(
  datos: ClaseDeSueloNueva,
): Promise<ClaseDeSuelo> {
  const respuesta = await apiFetch<{ data: ClaseDeSuelo }>("/api/suelo-clases", {
    method: "POST",
    body: JSON.stringify(datos),
  });

  return respuesta.data;
}

/**
 * Lo editable de una clase: su glosa y su posición.
 *
 * SIN `codigo`, y el servidor lo ignoraría igual. Es la identidad con la que el
 * seeder reconoce la fila —renombrarlo haría que la próxima siembra creara un
 * duplicado— y además es lo que el informe imprime: una clase renombrada
 * dejaría al selector ofreciendo dos opciones para la misma cosa. Un código mal
 * tipeado se da de baja y se crea el correcto.
 */
export interface ClaseDeSueloEditada {
  descripcion: string | null;
  orden: number;
}

export async function guardarClaseDeSuelo(
  id: number,
  cambios: ClaseDeSueloEditada,
): Promise<ClaseDeSuelo> {
  const respuesta = await apiFetch<{ data: ClaseDeSuelo }>(`/api/suelo-clases/${id}`, {
    method: "PUT",
    body: JSON.stringify(cambios),
  });

  return respuesta.data;
}

/**
 * Da de baja una clase del catálogo.
 *
 * El borrado es LÓGICO del lado del servidor: los informes que ya la eligieron
 * la siguen necesitando para que su relación resuelva. Lo que el informe
 * imprime no depende de esto — cada fila del cuadro guarda su propia copia
 * congelada del código.
 */
export async function darDeBajaClaseDeSuelo(id: number): Promise<void> {
  await apiFetch<void>(`/api/suelo-clases/${id}`, { method: "DELETE" });
}
